import React, { ReactElement, Ref } from 'react';

import PropTypes from 'prop-types';

import { MultiControl, Selected, SearchInput, Placeholder } from '../layout';
import { MultiControlProps } from '../layout/Control/MultiControl';
import { GenericItem } from '../types';

export type MultiAutocompleteControlProps<TItem extends GenericItem> =
  MultiControlProps<TItem> & {
    openMenu: () => void;
  };

const MultiAutocompleteControlWrapped = <TItem extends GenericItem>(
  {
    selectedItems,
    isOpen,
    disabled,
    openMenu,
    ...props
  }: MultiAutocompleteControlProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  const shouldShowPlaceholder =
    !isOpen && (!selectedItems || !selectedItems.length);

  return (
    <MultiControl
      ref={ref}
      isAutocomplete
      selectedItems={selectedItems}
      isOpen={isOpen}
      renderItems={({ itemToString }) =>
        !isOpen && (
          <Selected selectedItems={selectedItems} itemToString={itemToString} />
        )
      }
      renderInput={(inputProps) => <SearchInput {...inputProps} />}
      renderPlaceholder={(placeholderProps) =>
        shouldShowPlaceholder && <Placeholder {...placeholderProps} />
      }
      onClick={() => {
        !isOpen && !disabled && openMenu();
      }}
      disabled={disabled}
      {...props}
    />
  );
};

const MultiAutocompleteControl = React.forwardRef(
  MultiAutocompleteControlWrapped,
);

MultiAutocompleteControl.displayName = 'MultiAutocompleteControl';

MultiAutocompleteControl.propTypes = {
  /** Function to open menu.  Provided by useMultiAutocomplete hook */
  openMenu: PropTypes.func.isRequired,
};

export default MultiAutocompleteControl as <T extends GenericItem>(
  p: MultiAutocompleteControlProps<T> & {
    ref?: Ref<HTMLDivElement | HTMLLabelElement>;
  },
) => ReactElement;
